import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Calendar, MapPin, Clock, ExternalLink, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ExhibitionsPage = () => {
  const [activeTab, setActiveTab] = useState('current');
  const [exhibitions, setExhibitions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load exhibitions from the CMS. Ordering is server-side (position ASC,
  // updatedAt DESC) so the admin's drag-order and last-edited signal both
  // influence what visitors see.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/exhibitions`);
        const data = await res.json();
        if (!cancelled) setExhibitions(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) setExhibitions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const currentExhibitions  = exhibitions.filter((e) => e.status === 'current');
  const upcomingExhibitions = exhibitions.filter((e) => e.status === 'upcoming');
  const pastExhibitions     = exhibitions.filter((e) => e.status === 'past');

  // Backwards-compat: the past-exhibition template reads `status` as the
  // year pill and past rows store the year on `year`; alias for the JSX.
  currentExhibitions.forEach((e) => { if (!e.status_label) e.status_label = e.status; });
  upcomingExhibitions.forEach((e) => { if (!e.status_label) e.status_label = e.status; });

  const tabContent = {
    current: currentExhibitions,
    upcoming: upcomingExhibitions,
    past: pastExhibitions
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 border-b border-border">
        <div className="max-w-[1400px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-6 font-sans">
              Curated Exhibitions
            </p>
            
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-foreground mb-8 leading-[1.1]">
              Exhibitions
            </h1>
            
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl font-sans leading-relaxed mb-8">
              For nearly five decades, Zebra One Gallery has curated exceptional exhibitions 
              showcasing contemporary art, photography, and blue-chip artists in the heart of Hampstead.
            </p>

            <p className="text-base text-muted-foreground max-w-3xl font-sans leading-relaxed">
              From exclusive Kate Moss imagery featured in Vogue to our annual Women in Art Initiative, 
              our exhibitions celebrate artistic excellence and cultural relevance.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Exhibition Tabs */}
      <section className="py-12 px-6 bg-muted/20 border-b border-border sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex items-center justify-center gap-8">
            <button
              onClick={() => setActiveTab('current')}
              className={`font-sans text-sm uppercase tracking-[0.2em] pb-2 transition-all ${
                activeTab === 'current'
                  ? 'text-foreground border-b-2 border-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Current
            </button>
            <button
              onClick={() => setActiveTab('upcoming')}
              className={`font-sans text-sm uppercase tracking-[0.2em] pb-2 transition-all ${
                activeTab === 'upcoming'
                  ? 'text-foreground border-b-2 border-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Upcoming
            </button>
            <button
              onClick={() => setActiveTab('past')}
              className={`font-sans text-sm uppercase tracking-[0.2em] pb-2 transition-all ${
                activeTab === 'past'
                  ? 'text-foreground border-b-2 border-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Past Exhibitions
            </button>
          </div>
        </div>
      </section>

      {/* Current & Upcoming Exhibitions */}
      {(activeTab === 'current' || activeTab === 'upcoming') && (
        <section className="py-20 px-6">
          <div className="max-w-[1400px] mx-auto">
            <div className="space-y-16">
              {tabContent[activeTab].map((exhibition, index) => (
                <motion.div
                  key={exhibition.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="grid grid-cols-1 lg:grid-cols-2 gap-12 border-b border-border pb-16 last:border-b-0"
                >
                  {/* Exhibition Image */}
                  <div className="aspect-[4/3] bg-muted/30 border border-border flex items-center justify-center relative overflow-hidden group">
                    <div className="absolute top-6 left-6 z-10">
                      <span className="px-4 py-2 bg-foreground text-background text-xs uppercase tracking-wider font-sans">
                        {exhibition.status_label || exhibition.status}
                      </span>
                    </div>
                    {exhibition.image ? (
                      <img
                        src={exhibition.image}
                        alt={exhibition.title}
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700"
                        loading="lazy"
                      />
                    ) : (
                      <div className="text-center p-8">
                        <Calendar className="w-16 h-16 text-muted-foreground mx-auto mb-4 group-hover:scale-110 transition-transform" />
                        <p className="text-sm uppercase tracking-wider text-muted-foreground font-sans">
                          Exhibition Image
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Exhibition Details */}
                  <div className="flex flex-col justify-center">
                    <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-3">
                      {exhibition.title}
                    </h2>
                    
                    {exhibition.subtitle && (
                      <p className="font-serif text-xl text-muted-foreground italic mb-6">
                        {exhibition.subtitle}
                      </p>
                    )}

                    <div className="space-y-3 mb-8">
                      <div className="flex items-start gap-3">
                        <Calendar className="w-5 h-5 text-foreground flex-shrink-0 mt-1" />
                        <div>
                          <p className="text-sm uppercase tracking-wider text-foreground font-sans mb-1">Dates</p>
                          <p className="text-muted-foreground font-sans">{exhibition.dates}</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <MapPin className="w-5 h-5 text-foreground flex-shrink-0 mt-1" />
                        <div>
                          <p className="text-sm uppercase tracking-wider text-foreground font-sans mb-1">Location</p>
                          <p className="text-muted-foreground font-sans">{exhibition.location}</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <Clock className="w-5 h-5 text-foreground flex-shrink-0 mt-1" />
                        <div>
                          <p className="text-sm uppercase tracking-wider text-foreground font-sans mb-1">Gallery Hours</p>
                          <p className="text-muted-foreground font-sans">Tue-Sat: 10am-5pm</p>
                        </div>
                      </div>
                    </div>

                    <p className="text-muted-foreground font-sans leading-relaxed mb-8">
                      {exhibition.description}
                    </p>

                    <div className="flex flex-col sm:flex-row gap-4">
                      <Link to={exhibition.href || "/contact"}>
                        <motion.button
                          whileHover={{ scale: 1.02, x: 4 }}
                          whileTap={{ scale: 0.98 }}
                          className="px-6 py-3 bg-foreground text-background font-sans text-sm uppercase tracking-[0.2em] flex items-center gap-2"
                          data-testid={`exhibition-cta-${index}`}
                        >
                          {exhibition.cta || "Plan Your Visit"}
                          <ChevronRight className="w-4 h-4" />
                        </motion.button>
                      </Link>
                      <Link to="/shop">
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          className="px-6 py-3 border border-foreground text-foreground font-sans text-sm uppercase tracking-[0.2em]"
                        >
                          Browse Collection
                        </motion.button>
                      </Link>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Past Exhibitions Archive */}
      {activeTab === 'past' && (
        <section className="py-20 px-6">
          <div className="max-w-[1400px] mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-16 text-center"
            >
              <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-6">
                Exhibition Archive
              </h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto font-sans leading-relaxed">
                Selected highlights from nearly five decades of exhibitions at Zebra One Gallery.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {pastExhibitions.map((exhibition, index) => (
                <motion.div
                  key={exhibition.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-background p-8 border border-border group hover:border-foreground transition-colors"
                >
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <p className="text-3xl font-serif text-muted-foreground/30 mb-2">{exhibition.year}</p>
                      <h3 className="font-serif text-2xl text-foreground mb-2">
                        {exhibition.title}
                      </h3>
                      <p className="text-sm uppercase tracking-wider text-muted-foreground font-sans">
                        {exhibition.artist}
                      </p>
                    </div>
                  </div>

                  <p className="text-muted-foreground font-sans leading-relaxed">
                    {exhibition.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Press & Coverage */}
      <section className="py-20 px-6 bg-muted/20 border-t border-border">
        <div className="max-w-[1400px] mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-6">
              Press & Recognition
            </h2>
            
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-12 font-sans leading-relaxed">
              Our exhibitions have been featured in leading publications worldwide.
            </p>

            <div className="flex flex-wrap items-center justify-center gap-8 mb-12">
              {['Vogue', 'The Guardian', 'The Telegraph', 'Harper\'s Bazaar', 'InStyle', 'Vice', 'i-D Magazine', 'Dazed'].map((publication) => (
                <p key={publication} className="text-lg font-serif text-muted-foreground">
                  {publication}
                </p>
              ))}
            </div>

            <Link to="/news">
              <motion.button
                whileHover={{ scale: 1.02, x: 4 }}
                whileTap={{ scale: 0.98 }}
                className="px-6 py-3 border border-foreground text-foreground font-sans text-sm uppercase tracking-[0.2em] inline-flex items-center gap-2"
              >
                View Press Archive
                <ExternalLink className="w-4 h-4" />
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 bg-foreground text-background">
        <div className="max-w-[1400px] mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-serif text-3xl md:text-4xl mb-6">
              Visit Our Current Exhibition
            </h2>
            
            <p className="text-lg max-w-2xl mx-auto mb-12 font-sans leading-relaxed opacity-90">
              Experience exceptional contemporary art in person at our Hampstead gallery. 
              Open Tuesday to Saturday, 10am–5pm.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/contact">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="px-6 py-3 bg-background text-foreground font-sans text-sm uppercase tracking-[0.2em]"
                >
                  Plan Your Visit
                </motion.button>
              </Link>
              
              <a href="tel:02077941281">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="px-6 py-3 border border-background text-background font-sans text-sm uppercase tracking-[0.2em]"
                >
                  Call: 020 7794 1281
                </motion.button>
              </a>
            </div>

            <div className="mt-12 pt-12 border-t border-background/20">
              <p className="text-sm font-sans opacity-75">
                <strong>Zebra One Gallery</strong> · Perrins Court, 73a Hampstead High Street · London NW3 1QH
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ExhibitionsPage;
